import React, { useEffect, useState } from 'react';
import { useMutation } from 'react-query';

import { CalendarOutlined, CloudDownloadOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import styled from 'styled-components';

import { initiateRingCentralCommunicationBackfill } from 'apis/RingCentralAPI';
import { useRootContext } from 'components/layout/RootContext';
import Button from 'components/lib/Button';
import DatePicker from 'components/lib/DatePicker';
import Modal from 'components/lib/Modal';

import { RINGCENTRAL_BACKFILL_DEFAULT_LOOKBACK_DAYS } from './constants';

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 22px 0;
`;

const Description = styled.div`
  color: var(--text-secondary);
  font-size: 14px;
`;

const ImportSummary = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 14px 16px;
  border-left: 3px solid var(--button-blue);
  background: var(--background-gray);
`;

const ImportSummaryTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--text-primary);
  font-family: var(--font-bold);
  font-size: 14px;
`;

const ImportSummaryDescription = styled.div`
  color: var(--text-secondary);
  font-size: 13px;
`;

const Form = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  flex-wrap: wrap;
  gap: 12px;
`;

const Field = styled.label`
  display: grid;
  grid-template-columns: 96px minmax(0, 1fr);
  align-items: center;
  gap: 16px;
  color: var(--text-primary);
  font-family: var(--font-bold);
  font-size: 14px;
`;

const FieldControl = styled.div`
  min-width: 0;

  .ant-picker {
    width: 100%;
  }
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

type Props = {
  open: boolean;
  onClose: () => void;
};

const RingCentralCommunicationBackfillModal = ({ open, onClose }: Props) => {
  const [startDate, setStartDate] = useState<Dayjs | null>(() =>
    dayjs().subtract(RINGCENTRAL_BACKFILL_DEFAULT_LOOKBACK_DAYS, 'day')
  );
  const [endDate, setEndDate] = useState<Dayjs | null>(() => dayjs());
  const [isStartDatePickerOpen, setIsStartDatePickerOpen] = useState(false);
  const [isEndDatePickerOpen, setIsEndDatePickerOpen] = useState(false);
  const { showMessage } = useRootContext();
  const { mutateAsync: initiateBackfill, isLoading } = useMutation(initiateRingCentralCommunicationBackfill);

  const resetForm = () => {
    const defaultEndDate = dayjs();
    setStartDate(defaultEndDate.subtract(RINGCENTRAL_BACKFILL_DEFAULT_LOOKBACK_DAYS, 'day'));
    setEndDate(defaultEndDate);
    setIsStartDatePickerOpen(false);
    setIsEndDatePickerOpen(false);
  };

  const handleClose = () => {
    if (isLoading) {
      return;
    }

    resetForm();
    onClose();
  };

  const handleStartDateChange = (value: Dayjs | null) => {
    setStartDate(value);
    setTimeout(() => setIsStartDatePickerOpen(false), 0);
    if (endDate && value && endDate.isBefore(value, 'day')) {
      setEndDate(null);
    }
  };

  const handleEndDateChange = (value: Dayjs | null) => {
    setEndDate(value);
    setTimeout(() => setIsEndDatePickerOpen(false), 0);
  };

  const handleSubmit = async () => {
    if (!startDate || !endDate) {
      return;
    }

    try {
      await initiateBackfill({
        start_date: startDate.format('YYYY-MM-DD'),
        end_date: endDate.format('YYYY-MM-DD'),
      });
      showMessage('success', 'RingCentral communication ingestion scheduled.');
      resetForm();
      onClose();
    } catch {
      showMessage('error', 'Unable to schedule RingCentral communication ingestion.');
    }
  };

  useEffect(() => {
    if (!open) {
      resetForm();
    }
  }, [open]);

  return (
    <Modal
      open={open}
      title="Ingest RingCentral communications"
      destroyOnClose
      width={620}
      footer={
        <Footer>
          <Button disabled={isLoading} onClick={handleClose}>
            Cancel
          </Button>
          <Button type="primary" loading={isLoading} disabled={!startDate || !endDate} onClick={handleSubmit}>
            Start ingestion
          </Button>
        </Footer>
      }
      closable={!isLoading}
      maskClosable={!isLoading}
      onCancel={handleClose}
    >
      <Content>
        <Description>
          Queue a background import for RingCentral calls, SMS, and voicemail. Records appear in this list as they are
          processed.
        </Description>
        {startDate && endDate && (
          <ImportSummary>
            <ImportSummaryTitle>
              <CloudDownloadOutlined />
              {startDate.format('MMM D, YYYY')} through {endDate.format('MMM D, YYYY')}
            </ImportSummaryTitle>
            <ImportSummaryDescription>
              {endDate.diff(startDate, 'day') + 1} calendar days will be included. Existing communications are updated
              by provider ID, so this range can be run again safely.
            </ImportSummaryDescription>
          </ImportSummary>
        )}
        <Form>
          <Field>
            Start date
            <FieldControl>
              <DatePicker
                open={isStartDatePickerOpen}
                value={startDate}
                maxDate={dayjs()}
                onChange={handleStartDateChange}
                onOpenChange={setIsStartDatePickerOpen}
                suffixIcon={<CalendarOutlined />}
              />
            </FieldControl>
          </Field>
          <Field>
            End date
            <FieldControl>
              <DatePicker
                disabled={!startDate}
                open={isEndDatePickerOpen}
                value={endDate}
                minDate={startDate || undefined}
                maxDate={dayjs()}
                onChange={handleEndDateChange}
                onOpenChange={setIsEndDatePickerOpen}
                suffixIcon={<CalendarOutlined />}
              />
            </FieldControl>
          </Field>
        </Form>
      </Content>
    </Modal>
  );
};

export default RingCentralCommunicationBackfillModal;
